import React, { useMemo, useState } from 'react';
import { Search, Layers, X, ChevronRight, Briefcase, Landmark } from 'lucide-react';
import { ROLES } from '../data/framework';
import { ITEM_TYPE_LABEL, itemCount, questionnaireSize } from '../utils/framework';
import type { CompetencyDomain, ItemType } from '../types/framework';
export function CompetencyDomains() {
  const [roleId, setRoleId] = useState(ROLES[0].id);
  const [search, setSearch] = useState('');
  const [openDomainId, setOpenDomainId] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<ItemType>('characteristic');
  const role = ROLES.find(r => r.id === roleId) ?? ROLES[0];
  const query = search.trim().toLowerCase();
  const domains = useMemo(() => {
    if (!query) return role.domains;
    return role.domains.filter(d => d.name.toLowerCase().includes(query) || [...d.characteristic, ...d.behaviour].some(i => i.competency.toLowerCase().includes(query) || i.criterion.toLowerCase().includes(query)));
  }, [role, query]);
  const openDomain = role.domains.find(d => d.id === openDomainId) ?? null;
  const matchCount = (d: CompetencyDomain) => query ? [...d.characteristic, ...d.behaviour].filter(i => i.competency.toLowerCase().includes(query) || i.criterion.toLowerCase().includes(query)).length : 0;
  return <div className="animate-in fade-in duration-500" data-id="element-890" data-mp="a6c35ib87wpx">
      <div className="mb-6" data-id="element-891" data-mp="f7j8zbtt6lty">
        <h1 className="text-2xl font-semibold text-slate-900" data-id="element-892" data-mp="f7sykyaxyqz4">
          Competency Domains
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-893" data-mp="18tbhv32uk52">
          The competency framework for each role: its domains, and the
          Characteristic and Behaviour questions available in each.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-894" data-mp="1vlrmkgwlpktg">
        {/* Roles */}
        <aside className="w-full lg:w-64 shrink-0 space-y-5" data-id="element-895" data-mp="1zltjydvazs2x">
          {(['Executive', 'Board'] as const).map(group => <div key={group} data-id="element-896" data-mp="38l36ye8kg4bo">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 px-3 flex items-center" data-id="element-897" data-mp="33kozaxavmwe6">
                {group === 'Executive' ? <Briefcase className="h-3.5 w-3.5 mr-1.5" data-id="element-898" data-mp="2zjder66v11cu" /> : <Landmark className="h-3.5 w-3.5 mr-1.5" data-id="element-899" data-mp="2ullcpjci3yux" />}
                {group}
              </h2>
              <nav className="space-y-0.5" data-id="element-900" data-mp="3rcdtkj79duxr">
                {ROLES.filter(r => r.group === group).map(r => <button key={r.id} onClick={() => {
              setRoleId(r.id);
              setOpenDomainId(null);
            }} aria-current={r.id === role.id ? 'true' : undefined} className={`w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${r.id === role.id ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-901" data-mp="3nb23ydodx9pk">
                    <span className="truncate" data-id="element-902" data-mp="mbvqfvrenngx">{r.name}</span>
                    <span className="text-xs text-slate-400 ml-2" data-id="element-903" data-mp="me8fesvkyy3h">
                      {r.domains.length}
                    </span>
                  </button>)}
              </nav>
            </div>)}
        </aside>

        {/* Domains */}
        <div className="flex-1 min-w-0 space-y-4" data-id="element-904" data-mp="10bddo6p1yso6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-905" data-mp="15aztmnw9mn0o">
            <div data-id="element-906" data-mp="1a3asdnf9s2rn">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-907" data-mp="1a2rlr2v2l0sx">
                {role.name}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5" data-id="element-908" data-mp="1f2vta3e02l70">
                {role.domains.length} domains · {itemCount(role)} questions
                available · {questionnaireSize(role)} per questionnaire
              </p>
            </div>
          </div>

          <div className="relative" data-id="element-909" data-mp="1adpkmvt2b4ei">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-910" data-mp="1acxqlpf45eqc" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search domains and questions" placeholder={`Search ${role.name} domains and questions...`} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-911" data-mp="16d6parryng7k" />
          </div>

          {domains.length === 0 ? <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center bg-white" data-id="element-912" data-mp="11fnclwawvbhq">
              <Layers className="h-6 w-6 text-slate-400 mx-auto mb-3" data-id="element-913" data-mp="wdy2civij1up" />
              <h3 className="text-sm font-medium text-slate-900" data-id="element-914" data-mp="2cs7od6ca8eid">
                No matching domains
              </h3>
              <p className="text-sm text-slate-500 mt-1 mb-4" data-id="element-915" data-mp="27u662nmokj7y">
                Nothing in {role.name} matches “{search}”.
              </p>
              <button onClick={() => setSearch('')} className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-916" data-mp="3uqvxxv2lch6c">
                Clear search
              </button>
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-917" data-mp="daw6o23y4jtt">
              {domains.map(d => {
            const matches = matchCount(d);
            return <button key={d.id} onClick={() => {
              setOpenDomainId(d.id);
              setActiveType('characteristic');
            }} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 text-left hover:border-slate-300 transition-colors group" data-id="element-918" data-mp="he2a71zobwwl">
                    <div className="flex items-start justify-between gap-3" data-id="element-919" data-mp="111jgwu1c3ljj">
                      <h3 className="text-base font-semibold text-slate-900" data-id="element-920" data-mp="1auw49l83fke2">
                        {d.name}
                      </h3>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-600 shrink-0 mt-1" data-id="element-921" data-mp="15v9pv8gj3y3o" />
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-4" data-id="element-922" data-mp="vzpssiq8atdc">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100" data-id="element-923" data-mp="rxcolyr4r3io">
                        {d.characteristic.length} Characteristic
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-100" data-id="element-924" data-mp="i6qdagqg4z5s">
                        {d.behaviour.length} Behaviour
                      </span>
                      {matches > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-925" data-mp="8b6pyipy78sn">
                          {matches} match{matches === 1 ? '' : 'es'}
                        </span>}
                    </div>
                  </button>;
          })}
            </div>}
        </div>
      </div>

      {/* Domain drawer */}
      {openDomain && <div className="fixed inset-0 z-50 flex justify-end" data-id="element-926" data-mp="2ttk7wahfbwuj">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpenDomainId(null)} data-id="element-927" data-mp="1w7eir2xilifu" />
          <div role="dialog" aria-modal="true" aria-labelledby="domain-title" className="relative w-full max-w-xl bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-300" data-id="element-928" data-mp="216zgxb4v1mri">
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200" data-id="element-929" data-mp="1dgwr71of4sw2">
              <div data-id="element-930" data-mp="1ifgeb5er0aq0">
                <p className="text-xs text-slate-500" data-id="element-931" data-mp="1iey8bh2aai4w">{role.name}</p>
                <h2 id="domain-title" className="text-lg font-semibold text-slate-900" data-id="element-932" data-mp="bse30xivbt8n">
                  {openDomain.name}
                </h2>
              </div>
              <button onClick={() => setOpenDomainId(null)} aria-label="Close" className="text-slate-400 hover:text-slate-600 mt-1" data-id="element-933" data-mp="ktlnocsgovk9">
                <X className="h-5 w-5" data-id="element-934" data-mp="uqqbsi5vb13p" />
              </button>
            </div>
            <div className="px-6 pt-4 border-b border-slate-200" data-id="element-935" data-mp="ps6ooefjfj9r">
              <div className="flex gap-6" role="tablist" data-id="element-936" data-mp="prnexl01k16t">
                {(['characteristic', 'behaviour'] as ItemType[]).map(t => <button key={t} role="tab" aria-selected={activeType === t} onClick={() => setActiveType(t)} className={`pb-3 border-b-2 text-sm font-medium transition-colors ${activeType === t ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-937" data-mp="zhyp3pabj6m5">
                    {ITEM_TYPE_LABEL[t]}
                    <span className="ml-2 text-xs text-slate-400" data-id="element-938" data-mp="1x319p58o3ph1">
                      {openDomain[t].length}
                    </span>
                  </button>)}
              </div>
            </div>
            <ul className="flex-1 overflow-y-auto p-6 space-y-3" data-id="element-939" data-mp="1n7gb7h2t57c1">
              {openDomain[activeType].map(item => <li key={item.no} className="border border-slate-200 rounded-lg p-4" data-id="element-940" data-mp="1e4f5hhhuti8w">
                  <div className="flex items-center justify-between gap-3 mb-1" data-id="element-941" data-mp="zh3uphthy29u">
                    <span className="text-xs font-medium text-slate-500" data-id="element-942" data-mp="14e2p81h2ob8k">
                      {item.competency}
                    </span>
                    <span className="text-xs text-slate-400 shrink-0" data-id="element-943" data-mp="qfw7busiqluj">
                      #{item.no}
                    </span>
                  </div>
                  <p className="text-sm text-slate-900 leading-snug" data-id="element-944" data-mp="3092nvovieb96">
                    {item.criterion}
                  </p>
                </li>)}
            </ul>
          </div>
        </div>}
    </div>;
}